import { useBlocker } from 'react-router';
import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * Options for configuring unsaved changes detection behavior.
 */
export interface UseUnsavedChangesOptions {
  message?: string;
  initialValue?: boolean;
  onBeforeUnload?: () => void;
}

/**
 * Return type for unsaved changes hook with state and utility functions.
 */
export interface UseUnsavedChangesReturn {
  markAsDirty: () => void;
  markAsClean: () => void;
  isBlockerActive: boolean;
  hasUnsavedChanges: boolean;
  cancelNavigation: () => void;
  proceedNavigation: () => void;
  setHasUnsavedChanges: (value: boolean) => void;
  confirmNavigation: (proceed: () => void, onNeedConfirm?: () => void) => void;
}

const DEFAULT_MESSAGE = 'You have unsaved changes. Are you sure you want to leave?';

/**
 * Hook to track and manage unsaved changes with browser unload and in-app navigation blocking.
 * Uses useBlocker (data router) for in-app navigation and beforeunload for tab close/refresh.
 *
 * @param { UseUnsavedChangesOptions } options Configuration options.
 * @returns { UseUnsavedChangesReturn } Returns change tracking state and utilities.
 */
export const useUnsavedChanges = (options: UseUnsavedChangesOptions = {}): UseUnsavedChangesReturn => {
  const { onBeforeUnload, initialValue = false, message = DEFAULT_MESSAGE } = options;

  const messageRef = useRef(message);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(initialValue);

  messageRef.current = message;

  // Block in-app navigation via React Router data router
  const blocker = useBlocker(
    ({ nextLocation, currentLocation }) => hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
  );

  // Block browser tab close / refresh
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        onBeforeUnload?.();

        event.preventDefault();
      }
    };

    globalThis.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, onBeforeUnload]);

  const markAsDirty = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  const markAsClean = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  const proceedNavigation = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker]);

  const cancelNavigation = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  const confirmNavigation = useCallback(
    (proceed: () => void, onNeedConfirm?: () => void) => {
      if (hasUnsavedChanges) {
        if (onNeedConfirm) {
          onNeedConfirm();
        } else {
          const confirmed = globalThis.confirm(messageRef.current);

          if (confirmed) {
            setHasUnsavedChanges(false);
            proceed();
          }
        }
      } else {
        proceed();
      }
    },
    [hasUnsavedChanges],
  );

  return {
    markAsDirty,
    markAsClean,
    cancelNavigation,
    hasUnsavedChanges,
    confirmNavigation,
    proceedNavigation,
    setHasUnsavedChanges,
    isBlockerActive: blocker.state === 'blocked',
  };
};
